import dayjs from 'dayjs';

const RECORD_TYPE_TO_KIND = {
  AtsReview: 'review',
  AtsAssessment: 'assessment',
  AtsInterview: 'interview',
  AtsHackerrank: 'hackerrank',
  AtsHackerearth: 'hackerearth_assessment',
  AtsCriteriacorp: 'criteriacorp_assessment',
};

const mapScorecardToTableFormat = (sc, atsJobStages = []) => {
  const stage =
    sc.stage ||
    sc.ats_job_stage ||
    atsJobStages.find((s) => s.id === sc.ats_job_stage_id) ||
    {};
  const action = sc.ats_job_stage_action || {};
  const reviewer = sc.reviewer
    ? {
        ...sc.reviewer,
        avatar: sc.reviewer.avatar_url || sc.reviewer.avatar,
      }
    : null;
  const activityName =
    sc.stage_action_name || sc.record?.name || action.name || '';
  const kind = RECORD_TYPE_TO_KIND[sc.record_type] || action.kind || 'review';

  return {
    ...sc,
    submitted_at: sc.submitted_at ?? sc.updated_at,
    reviewer: reviewer || sc.submitted_by,
    submitted_by: sc.submitted_by || reviewer,
    ats_job_stage: {
      id: stage.id ?? sc.ats_job_stage_id,
      name: stage.name ?? '',
      position: stage.position ?? 0,
    },
    ats_job_stage_action: {
      id: action.id ?? sc.record?.id,
      name: activityName,
      position: action.position ?? Infinity,
    },
    data: {
      kind,
      time: sc.submitted_at || sc.created_at || sc.updated_at,
      id: sc.ats_job_stage_action_id ?? action.id ?? sc.record?.id,
      interviewTiming: {
        start_time: null,
        end_time: null,
      },
    },
  };
};

/**
 * Transforms API scorecards array to the format expected by the table config.
 * @param {Array} scorecards - Array of scorecards from API
 * @param {Object} applicationData - Application data for enriching stage/action info
 * @returns {Array}
 */
export const transformScorecardsToTableFormat = (
  scorecards,
  applicationData
) => {
  const list = scorecards || [];
  const atsJobStages = applicationData?.ats_job_stages || [];
  return list.map((sc) => mapScorecardToTableFormat(sc, atsJobStages));
};

export const extractScorecards = (data) => {
  if (!data.ats_job_stages) return { completed: [], pending: [] };

  const getTimeByKind = (action) => {
    const kind = action.kind;

    if (kind === 'interview') {
      return {
        start_time: action.data?.start_time || null,
        end_time: action.data?.end_time || null,
        time: action.data?.start_time || null,
      };
    }

    if (kind === 'assessment') {
      return {
        time: action.data?.submitted_at || action?.submitted_at || null,
      };
    }

    if (kind === 'hackerrank' || kind === 'criteriacorp_assessment') {
      return {
        time:
          action?.data?.submission?.completed_at ||
          action?.submission?.completed_at ||
          null,
      };
    }

    if (kind === 'review') {
      return {
        time: action.data?.created_at || null,
      };
    }

    return { time: null };
  };

  const completed = [];
  const pending = [];

  data.ats_job_stages.forEach((stage) => {
    stage.ats_job_stage_actions?.forEach((action) => {
      const kind = action.kind;

      if (kind === 'interview' && action.data?.meeting_status !== 'ended')
        return;
      if (kind === 'assessment' && action.data?.status !== 'submitted') return;
      if (kind === 'hackerrank' && action.data?.status !== 'external_completed')
        return;
      if (
        kind === 'criteriacorp_assessment' &&
        action.data?.status !== 'external_completed'
      )
        return;
      if (kind === 'review' && !action.data?.id) return;

      action.data?.scorecard?.forEach((scorecard) => {
        const timeData = getTimeByKind(action);
        const scorecardData = {
          ...scorecard,
          start_time: timeData.start_time || null,
          end_time: timeData.end_time || null,
          created_at: scorecard?.created_at || null,
          updated_at: scorecard?.updated_at || null,
          ats_job_stage: {
            id: stage.id,
            name: stage.name,
            position: stage.position,
          },
          ats_job_stage_action: {
            id: action.id,
            name:
              action?.data?.substituted_name ||
              action?.data?.name ||
              action.label,
            position: action.position ?? Infinity,
          },
          data: {
            ...timeData,
            kind,
            interviewTiming: {
              start_time: timeData.start_time || null,
              end_time: timeData.end_time || null,
            },
            id: action.data?.id || null,
          },
        };

        if (scorecard.rating && scorecard.status === 'submitted') {
          completed.push(scorecardData);
        } else {
          pending.push(scorecardData);
        }
      });
    });

    stage.adhoc_interviews?.forEach((interview) => {
      interview.scorecard?.forEach((scorecard) => {
        const start = interview.start_time || null;
        const end = interview.end_time || null;

        const scorecardData = {
          ...scorecard,
          ats_job_stage: {
            id: stage.id,
            name: stage.name,
            position: stage.position,
          },
          ats_job_stage_action: {
            name: interview?.substituted_name || interview?.name,
          },
          data: {
            kind: 'interview',
            start_time: start,
            end_time: end,
            time: start,
            interviewTiming: {
              start_time: start,
              end_time: end,
            },
            id: interview.id || null,
          },
        };

        if (scorecard.rating && scorecard.status === 'submitted') {
          completed.push(scorecardData);
        } else {
          pending.push(scorecardData);
        }
      });
    });
    // Adhoc assessments and hackerRank assessments
    stage.adhoc_assessments?.forEach((adhocAssessment) => {
      if (
        !['submitted', 'external_completed'].includes(adhocAssessment?.status)
      )
        return;

      const platformMap = {
        hackerrank: 'hackerrank',
        hackerearth: 'hackerearth_assessment',
        criteriacorp: 'criteriacorp_assessment',
      };

      const assessmentType =
        platformMap[adhocAssessment?.platform] || 'assessment';

      const mutatedAdhocAssessment = {
        ...adhocAssessment,
        kind: assessmentType,
      };

      mutatedAdhocAssessment.scorecard?.forEach((scorecard) => {
        const timeData = getTimeByKind(mutatedAdhocAssessment);
        const scorecardData = {
          ...scorecard,
          ats_job_stage: {
            id: stage?.id,
            name: stage?.name,
            position: stage?.position,
          },
          ats_job_stage_action: {
            name:
              mutatedAdhocAssessment?.substituted_name ||
              mutatedAdhocAssessment?.name,
          },
          data: {
            kind: mutatedAdhocAssessment?.kind,
            start_time: timeData?.time || null,
            // end_time: end,
            time: timeData?.time || null,
            // interviewTiming: {
            //   start_time: start,
            //   end_time: end,
            // },
            id: mutatedAdhocAssessment?.id || null,
          },
        };

        if (scorecard.rating && scorecard.status === 'submitted') {
          completed.push(scorecardData);
        } else {
          pending.push(scorecardData);
        }
      });
    });

    // Adhoc reviews
    stage.adhoc_reviews?.forEach((adhocReview) => {
      adhocReview.scorecard?.forEach((scorecard) => {
        const start = adhocReview?.created_at || null;

        const scorecardData = {
          ...scorecard,
          ats_job_stage: {
            id: stage?.id,
            name: stage?.name,
            position: stage?.position,
          },
          ats_job_stage_action: {
            name: adhocReview?.substituted_name || adhocReview?.name,
          },
          data: {
            kind: 'review',
            start_time: start,
            // end_time: end,
            time: start,
            // interviewTiming: {
            //   start_time: start,
            //   end_time: end,
            // },
            id: adhocReview?.id || null,
          },
        };

        if (scorecard.rating && scorecard.status === 'submitted') {
          completed.push(scorecardData);
        } else {
          pending.push(scorecardData);
        }
      });
    });
  });

  return { completed, pending };
};

export const sortScorecards = (scorecards, sortBy) => {
  const ratingOrder = ['strong_yes', 'yes', 'no', 'strong_no'];

  return [...scorecards].sort((a, b) => {
    // Transferred scorecards always at the bottom
    const aTransferred = a.is_transferred ? 1 : 0;
    const bTransferred = b.is_transferred ? 1 : 0;
    if (aTransferred !== bTransferred) return aTransferred - bTransferred;

    switch (sortBy) {
      case 'most_recent':
        return dayjs(b.data.time || 0).unix() - dayjs(a.data.time || 0).unix();
      case 'stage':
        if (a.ats_job_stage.position !== b.ats_job_stage.position) {
          return a.ats_job_stage.position - b.ats_job_stage.position;
        }
        return (
          (a.ats_job_stage_action?.position ?? Infinity) -
          (b.ats_job_stage_action?.position ?? Infinity)
        );
      case 'rating_highest_first':
        return ratingOrder.indexOf(a.rating) - ratingOrder.indexOf(b.rating);
      case 'rating_lowest_first':
        return ratingOrder.indexOf(b.rating) - ratingOrder.indexOf(a.rating);
      default:
        return 0;
    }
  });
};

/**
 * Maps UI sort option to API sort params.
 * API: sort_by = "activity_time" | "stage" | "rating" | "created_at", order = "asc" | "desc"
 */
export const mapSortOptionToApiParams = (sortOption) => {
  if (!sortOption?.value) return undefined;
  switch (sortOption.value) {
    case 'most_recent':
      return { sort_by: 'activity_time', order: 'desc' };
    case 'stage':
      return { sort_by: 'stage', order: 'asc' };
    case 'rating_highest_first':
      return { sort_by: 'rating', order: 'desc' };
    case 'rating_lowest_first':
      return { sort_by: 'rating', order: 'asc' };
    default:
      return { sort_by: 'created_at', order: 'desc' };
  }
};

/**
 * Default sort for application scorecard completed + pending tables.
 * Change this object to switch the initial sort; the Sort UI still lists all `sortOptions`.
 */
export const defaultApplicationScorecardSort = {
  label: 'Stage',
  value: 'stage',
  element: 'stage',
  order: 'asc',
};

/** Order matches typical UX: default option first, then alternatives */
export const sortOptions = [
  {
    label: 'Stage',
    value: 'stage',
    element: 'stage',
    order: 'asc',
  },
  {
    label: 'Most recent',
    value: 'most_recent',
    element: 'most_recent',
    order: 'asc',
  },
  {
    label: 'Rating (highest to lowest)',
    value: 'rating_highest_first',
    element: 'rating_highest_first',
    order: 'asc',
  },
  {
    label: 'Rating (lowest to highest)',
    value: 'rating_lowest_first',
    element: 'rating_lowest_first',
    order: 'asc',
  },
];
